import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import BlinkingDotText from '../../components/BlinkingDotText'
import moment from "moment";
import { fetchUtil } from "../../helpers/fetchUtil";

const ShopPage = () => {
    // Get user and token from Redux store
    const user = useSelector((state) => state.userAuth?.user);
    const token = useSelector((state) => state.userAuth?.token);
    
    // Component state
    const [isRedirected, setIsRedirected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shopUrl, setShopUrl] = useState(null);
    
    // Enhanced debugging
    console.log('ShopPage - Initial render');
    console.log('ShopPage - User state:', JSON.stringify(user, null, 2));
    console.log('ShopPage - Token state:', JSON.stringify(token, null, 2));
    console.log('ShopPage - MultipassUrl from user:', user?.multipassUrl);
    console.log('ShopPage - MultipassUrlExpiry from user:', user?.multipassUrlExpiry);
    
    // Check if multipass URL is expired or missing
    const isExpiredOrMissing = () => {
        if (!user?.multipassUrlExpiry || !user?.multipassUrl) {
            console.log('ShopPage - Multipass URL or expiry is missing');
            return true;
        }
        
        const expiryTime = moment(user.multipassUrlExpiry);
        const currentTime = moment();
        const isExpired = expiryTime.isBefore(currentTime);
        
        console.log('ShopPage - Multipass URL expiry check:');
        console.log('  - Current time:', currentTime.format());
        console.log('  - Expiry time:', expiryTime.format());
        console.log('  - Is expired:', isExpired);
        
        return isExpired;
    };
    
    // Fetch the user's IP address
    const getIpAddress = async () => {
        try {
            console.log('ShopPage - Fetching user IP address...');
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            console.log('ShopPage - User IP address:', data.ip);
            return data.ip;
        } catch (error) {
            console.error('ShopPage - Error getting IP address:', error);
            return '127.0.0.1'; // Fallback IP
        }
    };
    
    // Process multipass login - either use existing or regenerate
    const processMultipassLogin = async () => {
        try {
            setIsLoading(true);
            console.log('ShopPage - Processing multipass login...');
            
            // Make sure we have the user and token
            if (!user || !token || !token.id) {
                throw new Error('Missing user or token');
            }
            
            // Determine if we need to regenerate the multipass URL
            if (isExpiredOrMissing()) {
                // Need to regenerate the multipass URL
                console.log('ShopPage - Regenerating multipass URL...');
                
                // Get user IP address
                const userIp = await getIpAddress();
                
                // Make request to regenerate the multipass URL
                const response = await fetchUtil('/api/DaVinciUsers/regenerateMultipass', {
                    method: 'POST',
                    body: JSON.stringify({
                        email: user.email,
                        region: user.region || 'US',
                        ip: userIp
                    }),
                    token: token.id,
                    externalApi: true
                });
                
                console.log('ShopPage - Regenerate response:', JSON.stringify(response, null, 2));
                
                if (response?.multipassUrl) {
                    console.log('ShopPage - Successfully regenerated multipass URL');
                    console.log('ShopPage - New multipassUrl:', response.multipassUrl);
                    console.log('ShopPage - New expiry:', response.multipassUrlExpiry);
                    
                    // Save the new URL and open it
                    setShopUrl(response.multipassUrl);
                    setTimeout(() => {
                        console.log('ShopPage - Opening regenerated multipass URL in new tab...');
                        window.open(response.multipassUrl, '_blank');
                        setIsRedirected(true);
                    }, 500);
                } else {
                    throw new Error('Invalid response from regenerateMultipass');
                }
            } else {
                // We have a valid multipass URL, use it
                console.log('ShopPage - Using existing multipass URL');
                console.log('ShopPage - Existing URL:', user.multipassUrl);
                
                setShopUrl(user.multipassUrl);
                setTimeout(() => {
                    console.log('ShopPage - Opening existing multipass URL in new tab...');
                    window.open(user.multipassUrl, '_blank');
                    setIsRedirected(true);
                }, 500);
            }
        } catch (error) {
            console.error('ShopPage - Error in multipass process:', error);
            setError(error.message || 'Failed to generate Shopify login URL');
            
            // Fall back to regular shop URL if anything fails
            console.log('ShopPage - Falling back to regular shop URL');
            window.open('https://davinci-vaporizer-us.myshopify.com/shop', '_blank');
            setIsRedirected(true);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Run the multipass login process when component mounts
    useEffect(() => {
        console.log('ShopPage - useEffect triggered');
        processMultipassLogin();
        
        // Cleanup function
        return () => {
            console.log('ShopPage - Component unmounting');
        };
    }, [user, token]); // Re-run if user or token changes
    
    return (
        <div
            className="info"
            style={{
                backgroundColor: "transparent",
                textAlign: "center",
                display: "flex",
                height: "calc(100vh - 120px)",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            {error && (
                <div style={{ color: "#fff", marginBottom: "20px" }}>
                    Error: {error}
                </div>
            )}
            
            <BlinkingDotText
                text={"Redirecting to our store"}
                isRedirected={isRedirected}
                renderPostRedirection={() => (
                    <p style={{color: "#fff"}}>
                        {" "}
                        <a
                            href={shopUrl || "https://davinci-vaporizer-us.myshopify.com/shop"}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{color: "#fff"}}
                        >
                            Click here
                        </a>{" "}
                        if you are not redirected.
                    </p>
                )}
            />
        </div>
    )
}

export default ShopPage